import { faCircle, faSquare } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    IChartMetaIndicator,
    IIssueChartDataChild,
    IssueChartDataAnnotation,
    IssueChartDataArrowAnnotation,
    IssueChartDataEllipseAnnotation,
    IssueChartDataRectangleAnnotation,
    IssueChartDataTextAnnotation,
    IssueFrequencySpectrumChartDataChild,
    IssueFrequencySpectrumChartMeta,
    IssueFrequencyWaterfallChartDataChild,
    IssueFrequencyWaterfallChartMeta,
    IssueTimeDomainChartDataChild,
    IssueTimeDomainChartMeta,
    IssueTimeSeriesChartDataChild,
    IssueTimeSeriesChartMeta,
} from '@kcftech/prometheus-client';
import AddPlotPinSvg from 'assets/AddPlotPin.svg?react';
import { DateTimeDisplay } from 'components/date_time/DateTimeDisplay';
import { TimeRangeDisplay } from 'components/date_time/TimeRangeDisplay';
import { SvgIcon } from 'components/svg_icon/SvgIcon';
import { sdClient } from 'contexts/SDKClientContext';
import { addHours, subHours } from 'date-fns';
import { IssueChartDataMetaInfo } from 'pages/Desk/issues/chart_data/types';
import {
    BurstDataType,
    IndicatorAndColorInfo,
    LoadedIndicatorAndColorInfo,
    UnifiedChartMetadata,
} from 'pages/workbench/types';
import { ReactNode } from 'react';
import {
    AnnotationConfig,
    AnnotationCreationConfig,
    TextAnnotationConfig,
} from 'shared/svg_annotations/types';
import { useTheme } from 'styled-components';
import { ThemeType } from 'styles/colorMode';
import { tokens } from 'styles/tokens';
import { uniqueBy } from 'utils/array';
import { getTrendLineColorForIndex } from 'utils/colors';
import { promiseAllBatched } from 'utils/promise';
import { v4 } from 'uuid';

export type AnnotationToolType = 'text' | 'arrow' | 'ellipse' | 'rectangle';

export const DefaultAnnotationToolType = 'text';

export type AnnotationToolConfig = Readonly<{
    name: AnnotationToolType;
    icon: ReactNode;
}>;

export const AnnotationTools: AnnotationToolConfig[] = [
    { name: 'text', icon: <SvgIcon component={AddPlotPinSvg} width={18} height={18} /> },
    { name: 'arrow', icon: <FontAwesomeIcon icon={faArrowUpLeft} /> },
    { name: 'ellipse', icon: <FontAwesomeIcon icon={faCircle} /> },
    { name: 'rectangle', icon: <FontAwesomeIcon icon={faSquare} /> },
];

export type AnnotationColorConfig = Readonly<{
    primary: string;
    secondary: string;
    selection: string;
}>;

export const AnnotationColors: AnnotationColorConfig[] = [
    { primary: 'black', secondary: 'white', selection: 'white' },
    {
        primary: 'white',
        secondary: 'black',
        selection: tokens.border.default,
    },
    { primary: 'red', secondary: tokens.text.onColor, selection: 'white' },
    { primary: 'orange', secondary: tokens.text.onColor, selection: 'white' },
    { primary: 'green', secondary: tokens.text.onColor, selection: 'white' },
    { primary: 'blue', secondary: tokens.text.onColor, selection: 'white' },
    { primary: 'purple', secondary: tokens.text.onColor, selection: 'white' },
];

export const useDefaultAnnotationColor = () => {
    const { colorMode } = useTheme();
    // default to black for light mode, white for dark mode
    return colorMode === ThemeType.light ? AnnotationColors[0] : AnnotationColors[1];
};

export const addOrUpdateAnnotations = (
    annotations: AnnotationConfig[],
    annotation: AnnotationConfig,
) => [...annotations.filter(({ id }) => id !== annotation.id), annotation];

export const removeAnnotationAndRenumber = (
    annotations: AnnotationConfig[],
    annotationId: string,
) => {
    const annotationToRemove = annotations.find(({ id }) => id === annotationId);
    const filtered = annotations.filter(({ id }) => id !== annotationId);

    return annotationToRemove?.type === 'text'
        ? filtered.map((annotation) =>
              annotation.type === 'text' && annotation.number > annotationToRemove.number
                  ? { ...annotation, number: annotation.number - 1 }
                  : annotation,
          )
        : filtered;
};

export const renumberTextAnnotation = (
    current: AnnotationConfig[],
    config: TextAnnotationConfig,
    newNumber: number,
) => {
    const textAnnotations = current.filter(
        ({ type }) => type === 'text',
    ) as TextAnnotationConfig[];

    const target = textAnnotations.find(({ id }) => id === config.id);
    const destination = textAnnotations.find(({ number }) => number === newNumber);

    if (target && destination && target.id !== destination.id) {
        const move = newNumber > target.number ? 'down' : 'up';

        return current.map((annotation) => {
            if (annotation.type !== 'text') {
                return annotation;
            }
            if (annotation.id === target.id) {
                return { ...annotation, number: newNumber };
            }
            if (
                move === 'down' &&
                annotation.number > target.number &&
                annotation.number <= newNumber
            ) {
                return { ...annotation, number: annotation.number - 1 };
            }
            if (
                move === 'up' &&
                annotation.number >= newNumber &&
                annotation.number < target.number
            ) {
                return { ...annotation, number: annotation.number + 1 };
            }
            return annotation;
        });
    }

    return current;
};

export const findNextCitationNumber = (annotations: AnnotationConfig[]) =>
    annotations.filter(({ type }) => type === 'text').length + 1;

export type GetAnnotationCreationInfoOptions = Readonly<{
    annotations: AnnotationConfig[];
    type: AnnotationToolType;
    color: AnnotationColorConfig;
}>;

export const getAnnotationCreationInfo = ({
    annotations,
    type,
    color: { primary, secondary },
}: GetAnnotationCreationInfoOptions): AnnotationCreationConfig => {
    switch (type) {
        case 'arrow':
        case 'ellipse':
        case 'rectangle':
            return { id: v4(), type, color: primary };
        case 'text':
            return {
                id: v4(),
                type,
                color: primary,
                textColor: secondary,
                number: findNextCitationNumber(annotations),
            };
        default:
            throw new Error('Invalid annotation type');
    }
};

export const getAnnotationError = (annotation: AnnotationConfig): string | undefined => {
    switch (annotation.type) {
        case 'text':
            return annotation.text.trim().length === 0
                ? 'Description is required'
                : undefined;
        default:
            return undefined;
    }
};

export const getAnnotationConfigFromChartData = (
    chartAnnotation: IssueChartDataAnnotation,
): AnnotationConfig => {
    const baseProps = {
        id: v4(),
        color: chartAnnotation.Color,
    };
    if (chartAnnotation instanceof IssueChartDataArrowAnnotation) {
        return {
            type: 'arrow',
            ...baseProps,
            headX: chartAnnotation.HeadX,
            headY: chartAnnotation.HeadY,
            tailX: chartAnnotation.TailX,
            tailY: chartAnnotation.TailY,
        };
    }
    if (chartAnnotation instanceof IssueChartDataEllipseAnnotation) {
        return {
            type: 'ellipse',
            ...baseProps,
            x1: chartAnnotation.X1,
            y1: chartAnnotation.Y1,
            x2: chartAnnotation.X2,
            y2: chartAnnotation.Y2,
        };
    }
    if (chartAnnotation instanceof IssueChartDataRectangleAnnotation) {
        return {
            type: 'rectangle',
            ...baseProps,
            x1: chartAnnotation.X1,
            y1: chartAnnotation.Y1,
            x2: chartAnnotation.X2,
            y2: chartAnnotation.Y2,
        };
    }
    if (chartAnnotation instanceof IssueChartDataTextAnnotation) {
        return {
            type: 'text',
            ...baseProps,
            x: chartAnnotation.X,
            y: chartAnnotation.Y,
            number: chartAnnotation.PinNumber,
            angleDegrees: chartAnnotation.PinAngle,
            textColor: chartAnnotation.PinTextColor,
            text: chartAnnotation.Text,
        };
    }
    throw new Error('Unsupported annotation type');
};

const IndicatorBatchSize = 20;

const loadIndicatorOrUndefined = async (indicatorId: string) => {
    try {
        return await sdClient.getIndicator({ indicatorId });
    } catch {
        return undefined;
    }
};

export const getLoadedIndicatorAndColorInfo = async (
    info: IChartMetaIndicator[],
): Promise<LoadedIndicatorAndColorInfo[]> => {
    const indicatorQueries = info.map<() => Promise<LoadedIndicatorAndColorInfo>>(
        ({ Id, Color, Hierarchy }, index) =>
            async () => ({
                indicatorId: Id,
                indicator: Id ? await loadIndicatorOrUndefined(Id) : undefined,
                color: Color ?? getTrendLineColorForIndex(index),
                hierarchy: Hierarchy,
            }),
    );
    return promiseAllBatched(indicatorQueries, IndicatorBatchSize);
};

export const getIndicatorAndColorInfo = (
    info: IChartMetaIndicator[],
): IndicatorAndColorInfo[] =>
    info.map(({ Id, DataPlatformId, Color, Hierarchy }, index) => ({
        indicatorId: Id,
        dataPlatformIndicatorId: DataPlatformId,
        color: Color ?? getTrendLineColorForIndex(index),
        hierarchy: Hierarchy,
    }));

export const getChartDataMetaIndicators = (chartData: IIssueChartDataChild) => {
    if (chartData instanceof IssueFrequencySpectrumChartDataChild) {
        return [chartData.ChartMeta.Indicator];
    }
    if (chartData instanceof IssueFrequencyWaterfallChartDataChild) {
        return chartData.ChartMeta.Points;
    }
    if (chartData instanceof IssueTimeDomainChartDataChild) {
        return [chartData.ChartMeta.Indicator];
    }
    if (chartData instanceof IssueTimeSeriesChartDataChild) {
        return chartData.ChartMeta.Indicators;
    }
    throw new Error('Invalid chart type');
};

export const getChartDataMetaTimestamp = (chartData: IIssueChartDataChild) => {
    if (chartData instanceof IssueFrequencySpectrumChartDataChild) {
        return <DateTimeDisplay date={new Date(chartData.ChartMeta.Timestamp)} />;
    }
    if (chartData instanceof IssueFrequencyWaterfallChartDataChild) {
        return (
            <DateTimeDisplay
                date={new Date(chartData.ChartMeta.SelectedSeries.Timestamp)}
            />
        );
    }
    if (chartData instanceof IssueTimeDomainChartDataChild) {
        return <DateTimeDisplay date={new Date(chartData.ChartMeta.Timestamp)} />;
    }
    if (chartData instanceof IssueTimeSeriesChartDataChild) {
        return (
            <TimeRangeDisplay
                startDate={new Date(chartData.ChartMeta.StartTimestamp)}
                endDate={new Date(chartData.ChartMeta.EndTimestamp)}
            />
        );
    }
    throw new Error('Invalid chart type');
};

export const getChartDataMetaIndicatorsFromMetaInfo = (
    chartData: IssueChartDataMetaInfo,
) => {
    switch (chartData.type) {
        case 'time-series':
            return chartData.meta.Indicators;
        case 'time-domain':
            return [chartData.meta.Indicator];
        case 'frequency-spectrum':
            return [chartData.meta.Indicator];
        case 'frequency-waterfall':
            return chartData.meta.Points;
        default:
            throw new Error('Invalid chart type');
    }
};

export const getChartDataFromMetaInfo = (
    chartDataMeta: IssueChartDataMetaInfo,
): IIssueChartDataChild => {
    switch (chartDataMeta.type) {
        case 'time-series': {
            const chartData = new IssueTimeSeriesChartDataChild();
            chartData.ChartMeta = chartDataMeta.meta as IssueTimeSeriesChartMeta;
            return chartData;
        }
        case 'time-domain': {
            const chartData = new IssueTimeDomainChartDataChild();
            chartData.ChartMeta = chartDataMeta.meta as IssueTimeDomainChartMeta;
            return chartData;
        }
        case 'frequency-spectrum': {
            const chartData = new IssueFrequencySpectrumChartDataChild();
            chartData.ChartMeta = chartDataMeta.meta as IssueFrequencySpectrumChartMeta;
            return chartData;
        }
        case 'frequency-waterfall': {
            const chartData = new IssueFrequencyWaterfallChartDataChild();
            chartData.ChartMeta = chartDataMeta.meta as IssueFrequencyWaterfallChartMeta;
            return chartData;
        }
        default:
            throw new Error('Invalid chart type');
    }
};

/**
 * Gets metadata given a chart data child
 * @param chartData The chart data to get the unified metadata for
 * @param timeRangeToNow Whether to override the end date of the time range to the current date
 * @returns Unified chart metadata
 */
export const getChartMeta = async (
    chartData: IIssueChartDataChild,
    timeRangeToNow = false,
): Promise<UnifiedChartMetadata> => {
    const overrideEndDate = timeRangeToNow ? new Date().valueOf() : undefined;

    if (chartData instanceof IssueTimeSeriesChartDataChild) {
        return {
            indicators: await getLoadedIndicatorAndColorInfo(
                chartData.ChartMeta.Indicators,
            ),
            timeRange: {
                type: 'custom',
                id: 'chart-data',
                startTimestamp: chartData.ChartMeta.StartTimestamp,
                endTimestamp: overrideEndDate ?? chartData.ChartMeta.EndTimestamp,
            },
            selectedPoint:
                chartData.ChartMeta.SelectedTimestamp &&
                chartData.ChartMeta.SelectedIndicatorId
                    ? {
                          xValue: chartData.ChartMeta.SelectedTimestamp,
                          seriesId: chartData.ChartMeta.SelectedIndicatorId,
                          isDownsampled: false,
                      }
                    : undefined,
        };
    }
    if (
        chartData instanceof IssueFrequencySpectrumChartDataChild ||
        chartData instanceof IssueTimeDomainChartDataChild
    ) {
        const selectedDate = new Date(chartData.ChartMeta.Timestamp);
        const burstDataType = chartData.ChartMeta.UnitType.toLowerCase() as BurstDataType;

        return {
            indicators: await getLoadedIndicatorAndColorInfo([
                chartData.ChartMeta.Indicator,
            ]),
            timeRange: {
                type: 'custom',
                id: 'chart-data',
                startTimestamp: subHours(selectedDate, 12).getTime(),
                endTimestamp: overrideEndDate ?? addHours(selectedDate, 12).getTime(),
            },
            selectedPoint: chartData.ChartMeta.Indicator.Id
                ? {
                      xValue: chartData.ChartMeta.Timestamp,
                      seriesId: chartData.ChartMeta.Indicator.Id,
                      isDownsampled: false,
                  }
                : undefined,
            frequencyBurstDataType: burstDataType,
            timeBurstDataType: burstDataType,
        };
    }
    if (chartData instanceof IssueFrequencyWaterfallChartDataChild) {
        const timestamps = chartData.ChartMeta.Points.map((point) => point.Timestamp);
        const minDate = new Date(Math.min(...timestamps));
        const maxDate = new Date(Math.max(...timestamps));

        return {
            indicators: await getLoadedIndicatorAndColorInfo(
                uniqueBy(chartData.ChartMeta.Points, (indicator) => indicator.Id),
            ),
            timeRange: {
                type: 'custom',
                id: 'chart-data',
                startTimestamp: subHours(minDate, 12).getTime(),
                endTimestamp: overrideEndDate ?? addHours(maxDate, 12).getTime(),
            },
            selectedPoint: {
                xValue: chartData.ChartMeta.SelectedSeries.Timestamp,
                seriesId: chartData.ChartMeta.SelectedSeries.IndicatorId,
                isDownsampled: false,
            },
            waterfallBurstDataType:
                chartData.ChartMeta.UnitType.toLowerCase() as BurstDataType,
            waterfallFrequencyPoints: chartData.ChartMeta.Points.filter(
                (point) =>
                    point.Id !== chartData.ChartMeta.SelectedSeries.IndicatorId ||
                    point.Timestamp !== chartData.ChartMeta.SelectedSeries.Timestamp,
            ).map((point) => {
                if (point.Id === undefined) {
                    throw new Error(
                        'Attempted to load chart data with a missing prom id',
                    );
                }

                return {
                    xValue: point.Timestamp,
                    seriesId: point.Id,
                    isDownsampled: false,
                };
            }),
        };
    }

    throw new Error('Invalid chart data type');
};
